import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { QuoteOutput } from '../components/LDU/QuoteOutput';
import { type CurrentPolicyContext } from '../types';

/**
 * Client-side PDF generation (CDN-free, PR #5j).
 *
 * Renders QuoteOutput into a hidden, same-origin iframe, clones the host
 * document's own compiled stylesheets into that iframe so Tailwind utility
 * classes and the Human Risks brand tokens resolve, captures each page with
 * html2canvas, and emits a multi-page A4 PDF.
 *
 * Why clone host styles instead of the old cdn.tailwindcss.com runtime:
 *   - The CDN was the last unvetted third-party script in the app and was in
 *     fact blocked by the production CSP (`script-src 'self'`), so PDF export
 *     could not work in production at all.
 *   - The app's first-party compiled CSS already contains every utility
 *     QuoteOutput uses (it is scanned by Tailwind), the `@theme` brand colours
 *     (hr-indigo, hr-pink, …), and the self-hosted Inter @font-face rules.
 *   - Stylesheets are same-origin (`style-src 'self'`) and inline <style>
 *     blocks are permitted (`'unsafe-inline'`), so cloning is CSP-clean.
 *
 * Remaining debt (docs/MIGRATION.md): output is raster (PNG-in-PDF). True
 * vector output and removing the iframe entirely are deferred to the
 * server-side Browser Rendering migration.
 */

const RENDER_TIMEOUT_MS = 30_000;

const pollUntil = async (predicate: () => boolean): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < RENDER_TIMEOUT_MS) {
    if (predicate()) return true;
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
  }
  return false;
};

/**
 * Serialise the host document's stylesheets so they can be replayed inside
 * the iframe. In production these are `<link>` elements pointing at the
 * hashed first-party CSS bundle; in Vite dev they are inline `<style>` tags.
 * Both are captured so PDF export is styled identically in either mode.
 */
const collectHostStyles = (): string =>
  Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

export const generateQuotePDF = async (context: CurrentPolicyContext): Promise<void> => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'absolute';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  iframe.style.width = '210mm';
  iframe.style.height = '297mm';
  document.body.appendChild(iframe);

  const iframeWindow = iframe.contentWindow;
  const iframeDoc = iframe.contentDocument ?? iframeWindow?.document;

  if (!iframeDoc || !iframeWindow) {
    document.body.removeChild(iframe);
    throw new Error('PDF generation failed: iframe context unavailable.');
  }

  iframeDoc.open();
  iframeDoc.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        ${collectHostStyles()}
        <style>
          body { margin: 0; padding: 0; background: white; }
          svg { display: block; }
        </style>
      </head>
      <body>
        <div id="pdf-root"></div>
      </body>
    </html>
  `);
  iframeDoc.close();

  // Wait until every cloned <link> stylesheet has parsed. Same-origin sheets
  // expose a non-null `.sheet` once loaded; anything that throws on access is
  // cross-origin (none expected) and must not block the render.
  const stylesReady = await pollUntil(() => {
    const links = Array.from(iframeDoc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
    return links.every((link) => {
      try {
        return link.sheet !== null;
      } catch {
        return true;
      }
    });
  });
  if (!stylesReady) {
    document.body.removeChild(iframe);
    throw new Error('PDF generation failed: stylesheets did not load.');
  }

  const rootEl = iframeDoc.getElementById('pdf-root');
  if (!rootEl) {
    document.body.removeChild(iframe);
    throw new Error('PDF generation failed: render root missing.');
  }

  const root = createRoot(rootEl);
  root.render(<QuoteOutput context={context} />);

  // Inter is self-hosted via the cloned @font-face rules; wait for it to be
  // ready so html2canvas captures the correct typeface (avoids FOIT capture).
  if (iframeDoc.fonts && typeof iframeDoc.fonts.ready?.then === 'function') {
    await iframeDoc.fonts.ready;
  }
  // One frame to let React commit + styles apply before capture.
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  try {
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    const pages = iframeDoc.querySelectorAll<HTMLElement>('.pdf-page');

    for (let i = 0; i < pages.length; i += 1) {
      const page = pages[i];
      if (!page) continue;

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: page.scrollWidth,
        windowHeight: page.scrollHeight,
      });

      // PNG keeps text crispness vs. JPEG. The server-side migration will
      // replace this whole pipeline with a vector PDF generator.
      const imgData = canvas.toDataURL('image/png');

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    }

    const safeName =
      context.client_profile.companyName.replace(/[^a-zA-Z0-9_-]+/g, '_') || 'Client';
    const filename = `QUOTE_${context.quote_meta.ref}_${safeName}.pdf`;
    pdf.save(filename);
  } finally {
    root.unmount();
    document.body.removeChild(iframe);
  }
};
